import React, { useState, useEffect } from "react";
import useApi from "../hooks/useApi";
import { baseUrl } from "../config/apiConfig";
import ErrorPopUpComponent from "../components/ErrorPopUpComponent";
import { isPlatformAdmin } from "../util/accessControl";
import useAuth from "../hooks/useAuth";

/* ─── sub-components ─────────────────────────────────────────── */

const Spinner = () => (
  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
);

/* ─── main page ──────────────────────────────────────────────── */
const AdminOrgsPage = () => {
  const { callApi, loading } = useApi();
  const [orgs, setOrgs] = useState([]);

  // add-org form
  const [newOrgName, setNewOrgName] = useState("");
  const [addLoading, setAddLoading] = useState(false);

  // edit inline state  { [id]: string }
  const [editNames, setEditNames] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // org id being toggled / saved

  // errors
  const [errorMessage, setErrorMessage] = useState("");
  const [isMessageShow, setIsMessageShow] = useState(false);

  const { user } = useAuth();
  const authorized = isPlatformAdmin(user);

  useEffect(() => {
    if (authorized) fetchOrgs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized]);

  /* ── api calls ── */
  const fetchOrgs = async () => {
    try {
      const res = await callApi(`${baseUrl}/orgs/`, "GET");
      if (res?.data) setOrgs(res.data);
    } catch (err) {
      showError(err.message || "Failed to fetch organizations");
    }
  };

  const handleAddOrg = async (e) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;
    setAddLoading(true);
    try {
      await callApi(`${baseUrl}/orgs/`, "POST", { name: newOrgName.trim() });
      setNewOrgName("");
      await fetchOrgs();
    } catch (err) {
      showError(err.message || "Failed to add organization");
    } finally {
      setAddLoading(false);
    }
  };

  const handleSaveName = async (org) => {
    const name = (editNames[org.id] || "").trim();
    if (!name || name === org.name) {
      setEditingId(null);
      return;
    }
    setActionLoading(`save-${org.id}`);
    try {
      await callApi(`${baseUrl}/orgs/${org.id}`, "PUT", { name });
      await fetchOrgs();
      setEditingId(null);
    } catch (err) {
      showError(err.message || "Failed to rename organization");
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleActive = async (org) => {
    setActionLoading(`toggle-${org.id}`);
    try {
      await callApi(`${baseUrl}/orgs/${org.id}`, "PUT", {
        active: !org.active,
      });
      await fetchOrgs();
    } catch (err) {
      showError(err.message || "Failed to update organization status");
    } finally {
      setActionLoading(null);
    }
  };

  const showError = (msg) => {
    setErrorMessage(msg);
    setIsMessageShow(true);
  };

  /* ── unauthorized view ── */
  if (!authorized) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center p-10 bg-white shadow-xl rounded-2xl max-w-sm w-full border border-red-100">
          <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-500 text-sm">
            You don't have permission to manage organizations.
          </p>
        </div>
      </div>
    );
  }

  /* ── main view ── */
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50/40 pt-24 pb-10 px-4">
      <div className="max-w-5xl mx-auto space-y-8">

        {/* ── page header ── */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-600 text-white rounded-xl flex items-center justify-center shadow-md">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m3-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Organization Management</h1>
            <p className="text-sm text-gray-500 mt-0.5">Create, rename, and toggle org status</p>
          </div>
        </div>

        {/* ── add org form ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-sm font-semibold text-indigo-700 uppercase tracking-wider mb-4">
            Add New Organization
          </h2>
          <form onSubmit={handleAddOrg} className="flex gap-3">
            <div className="relative flex-1">
              <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </span>
              <input
                id="new-org-name"
                type="text"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="Organization name"
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white transition"
                required
              />
            </div>
            <button
              id="add-org-btn"
              type="submit"
              disabled={addLoading}
              className="px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 min-w-[110px] justify-center"
            >
              {addLoading ? <Spinner /> : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Org
                </>
              )}
            </button>
          </form>
        </div>

        {/* ── orgs list ── */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-800">Organizations</h2>
            <span className="bg-indigo-50 text-indigo-600 text-xs font-semibold py-1 px-3 rounded-full">
              {orgs.length} total
            </span>
          </div>

          {loading && orgs.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
          ) : orgs.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200">
              <svg className="mx-auto h-10 w-10 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m3-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p className="text-gray-500 font-medium">No organizations yet</p>
              <p className="text-gray-400 text-sm mt-1">Add one above to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {orgs.map((org) => {
                const isEditing = editingId === org.id;
                const isSaving = actionLoading === `save-${org.id}`;
                const isToggling = actionLoading === `toggle-${org.id}`;

                return (
                  <div
                    key={org.id}
                    className={`bg-white rounded-2xl border transition-all duration-200 p-5 flex flex-col gap-4 shadow-sm hover:shadow-md ${
                      org.active ? "border-gray-100" : "border-gray-200 opacity-75"
                    }`}
                  >
                    {/* ── colored card header ── */}
                    <div className={`-m-5 mb-0 rounded-t-2xl px-4 pt-4 pb-3 flex items-start justify-between ${
                      org.active
                        ? "bg-gradient-to-r from-[#69afee] to-[#096dea]"
                        : "bg-gradient-to-r from-gray-400 to-gray-500"
                    }`}>
                      {/* avatar + ID */}
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center font-bold text-lg text-white">
                          {org.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-white/80 text-xs font-semibold tracking-wide">
                          #{org.id}
                        </span>
                      </div>

                      {/* edit icon + toggle — white variants */}
                      <div className="flex items-center gap-2">
                        {/* pencil */}
                        <button
                          id={`edit-org-${org.id}`}
                          onClick={() => {
                            setEditingId(org.id);
                            setEditNames((prev) => ({ ...prev, [org.id]: org.name }));
                          }}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/20 transition"
                          title="Rename"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536M9 11l6.364-6.364a2 2 0 012.828 2.828L11.828 13.828a4 4 0 01-1.414.94l-3.536.707.707-3.536A4 4 0 019 11z" />
                          </svg>
                        </button>

                        {/* toggle */}
                        <button
                          id={`toggle-org-${org.id}`}
                          onClick={() => handleToggleActive(org)}
                          disabled={isToggling}
                          title={org.active ? "Deactivate" : "Activate"}
                          className="relative inline-flex items-center disabled:opacity-60"
                        >
                          {isToggling ? (
                            <div className="w-10 h-5 flex items-center justify-center">
                              <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                            </div>
                          ) : (
                            <div className={`w-10 h-5 rounded-full transition-colors duration-200 ${
                              org.active ? "bg-white/30" : "bg-black/20"
                            }`}>
                              <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 mt-0.5 ${
                                org.active ? "translate-x-5" : "translate-x-0.5"
                              }`} />
                            </div>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* ── card body ── */}
                    <div className="pt-4 flex flex-col gap-3 flex-1">
                      {/* name / inline edit */}
                      {isEditing ? (
                        <div className="flex gap-2 items-center">
                          <input
                            id={`edit-name-${org.id}`}
                            type="text"
                            value={editNames[org.id] ?? org.name}
                            onChange={(e) =>
                              setEditNames((prev) => ({ ...prev, [org.id]: e.target.value }))
                            }
                            className="flex-1 text-sm border border-indigo-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveName(org);
                              if (e.key === "Escape") setEditingId(null);
                            }}
                          />
                          <button
                            id={`save-name-${org.id}`}
                            onClick={() => handleSaveName(org)}
                            disabled={isSaving}
                            className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-1"
                          >
                            {isSaving ? <Spinner /> : "Save"}
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="text-xs px-2 py-1.5 text-gray-500 hover:text-gray-700"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <h3 className="text-base font-bold text-gray-800 truncate">{org.name}</h3>
                      )}

                      {/* footer: users button */}
                      <div className="mt-auto pt-3 border-t border-gray-100">
                        <button
                          id={`manage-users-${org.id}`}
                          onClick={() => window.location.href = `/admin/orgs/${org.id}/users`}
                          className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg px-3 py-2 transition"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                          Manage Users
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ErrorPopUpComponent
        isErrorShow={isMessageShow}
        errorMessage={errorMessage}
        redirect=""
      />
    </div>
  );
};

export default AdminOrgsPage;
